import { inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, OnInitEffects, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  map,
  mergeMap,
  tap,
} from 'rxjs/operators';
import { UserAdapter } from '../../adapters/user.adapter';
import { AppState } from '../../app.reducer';
import { selectLoggedIn } from '../auth/auth.reducer';
import { SubscriptionEffectInit } from '../subscription/subscription.effects';
import {
  finishOnboarding,
  loadUserAccount,
  loadUserAccountFailure,
  loadUserAccountSuccess,
  updateUserAccount,
  updateUserAccountFailure,
  updateUserAccountSuccess,
} from './user-account.actions';

@UntilDestroy()
@Injectable()
export class UserAccountEffects implements OnInitEffects {
  private readonly actions$ = inject(Actions);
  private readonly userAdapter = inject(UserAdapter);
  private readonly store = inject(Store<AppState>);
  
  ngrxOnInitEffects(): Action {
    this.store
      .select(selectLoggedIn)
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((loggedIn) => {
        if (loggedIn) {
          this.store.dispatch(loadUserAccount());
        }
      });
    return SubscriptionEffectInit();
  }

  loadUserAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserAccount),
      exhaustMap(() =>
        this.userAdapter.getUser().pipe(
          map((account) => loadUserAccountSuccess({ account })),
          catchError((error) => {
            return of(loadUserAccountFailure({ error }));
          })
        )
      )
    )
  );

  updateUserAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserAccount),
      mergeMap((props) =>
        this.userAdapter.editUser(props.account).pipe(
          map(() => updateUserAccountSuccess({ account: props.account })),
          catchError((error) => {
            return of(updateUserAccountFailure({ error }));
          })
        )
      )
    )
  );

  loadUserAccountFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadUserAccountFailure),
        tap((action) => {
          // Todo: Add proper error handling. Maybe with a toasty?
          //alert(action.error.message);
        })
      ),
    { dispatch: false }
  );

  finishOnboarding$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(finishOnboarding),
        exhaustMap(() => this.userAdapter.finishOnboarding())
      ),
    { dispatch: false }
  );
}
